import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { envApiUrl } from './envApiUrl';

export type EnabledServicesTypes = 'salary' | 'myday' | 'passwords' | 'simplified' | 'health';

export type UserType = {
  id: number;
  disabled: boolean;
  username: string;
  firstname: string;
  lastname: string;
  language?: string;
  address?: string;
  nationality?: string;
  dateOfBirth?: string | Date;
  gender?: string;
  height?: number;
  weight?: number;
  healthConditions?: string;
  bloodType?: string;
  email: string;
  role: 'USER' | 'ADMIN' | 'MASTER'; // adjust string union as needed
  description: string | null;
  lastlogin: string; // ISO timestamp
  lastloginip: string;
  hourRate: number;
  services: EnabledServicesTypes[];
  settings: {
    profilePicURL?: string; // Keep for backward compatibility during migration
    profilePictureFileId?: number; // New field for filesystem storage
    preferredFont?: string; // User's preferred font for notes
  };
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  deletedAt: string | null;
  lastSignin: {
    id: number;
    loginTime: string; // ISO timestamp
    ipAddress: string;
    browser: string;
    deviceType: string;
    loginStatus: string;
    os: string;
    metadata: {
      method: string;
      url: string;
      params: Record<string, unknown>;
      query: Record<string, unknown>;
      headers: Record<string, string>;
      ip: string;
      protocol: string;
      secure: boolean;
      baseUrl: string;
      path: string;
      session: Record<string, unknown>;
      geolocation?: {
        country?: string;
        countryCode?: string;
        region?: string;
        city?: string;
        timezone?: string;
        isp?: string;
        organization?: string;
        isVPN?: boolean;
        isTor?: boolean;
        isProxy?: boolean;
        isHosting?: boolean;
        threatLevel?: 'low' | 'medium' | 'high';
      };
      browser?: {
        browser: string;
        browserVersion: string;
        engine: string;
        engineVersion: string;
        os: string;
        osVersion: string;
        device: string;
        deviceType: 'Mobile' | 'Tablet' | 'Desktop';
        isMobile: boolean;
        isTablet: boolean;
        isDesktop: boolean;
        isBot: boolean;
        isCrawler: boolean;
        isTouchDevice: boolean;
        screenResolution?: string;
        language: string;
        timezone: string;
        platform: string;
      };
      clientInfo?: {
        ip: string;
        geolocation: any;
        timestamp: string;
        userAgent: string;
        acceptLanguage: string;
        referer: string;
        origin: string;
        host: string;
        protocol: string;
        secure: boolean;
      };
    };
    httpReferrer: string;
    errorMsg: string | null;
    createdAt: string; // ISO timestamp
    updatedAt: string; // ISO timestamp
    userId: number;
  };
};

export const useUserProfile = () => {
  const query = useQuery<UserType, Error>({
    queryKey: ['userProfile'],
    queryFn: async (): Promise<UserType> => {
      try {
        const response = await axios.get<UserType>(envApiUrl + '/users/profile', {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'x-csrf-token',
        });

        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          console.error('Authentication error:', error.response.data?.message || 'Access denied');
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
  });

  // Computed properties based on the user data
  const user = query.data;
  const userHasServiceSimplified = user?.services?.includes('simplified') || false;
  const userHasServiceHealth = user?.services?.includes('health') || false;
  const userHasServicePasswords = user?.services?.includes('passwords') || false;
  const userHasServiceSalary = user?.services?.includes('salary') || false;
  const userHasServiceMydays = user?.services?.includes('myday') || false;
  const userIsMaster = user?.role === 'MASTER';
  const userIsAdmin = user?.role === 'ADMIN';
  const userIsUser = user?.role === 'USER';
  const fullName = user ? `${user.firstname} ${user.lastname}` : '';
  const displayName = user?.firstname || user?.username || '';
  const isActive = user ? !user.disabled : false;
  const hasAnyServices = user?.services && user.services.length > 0;
  const initials = user ? `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase() : '';
  const lastLoginFormatted = user ? new Date(user.lastlogin).toLocaleDateString() : '';
  const accountAge = user ? Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0;

  return {
    // Original query properties
    ...query,
    // Computed properties
    userHasServiceSimplified,
    userHasServiceHealth,
    userHasServicePasswords,
    userHasServiceSalary,
    userHasServiceMydays,
    userIsMaster,
    userIsAdmin,
    userIsUser,
    fullName,
    displayName,
    isActive,
    hasAnyServices,
    initials,
    lastLoginFormatted,
    accountAge,
  };
};
